import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, Loader2, ArrowRight, Check, X, AlertTriangle, Download, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import {
  fetchProject, initSession, track, trackAuth,
  getPreviewUrl, getFullUrl, submitJoin, recordReturnVisit, getLang,
  optInFutureUpdates, isFanIdentified, getPrefsLink, getDeviceInfo,
} from "@/lib/api";
import { useI18n } from "@/i18n";
import LanguageSelector from "@/components/LanguageSelector";

// ── FANLY Fan-experience palette ────────────────────────────────────────
// Base surfaces + journey-state accents. #FF2A2A stays a secondary brand accent only.
const C = {
  bg: "#0B0D12",       // app background
  surface: "#151923",  // elevated surface
  indigo: "#5B6CFF",   // PREVIEW state
  coral: "#FF6B6B",    // UNLOCK cta
  mint: "#55E6B3",     // CONNECTED / unlocked
};

const SECTION_ORDER = ["the_story", "the_lyrics", "from_the_studio", "the_visual", "artist_bio"];
const fmt = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

const forcePlaybackSession = () => {
  // iOS 17+: make audio ignore the silent switch (incl. Instagram/Facebook in-app browsers).
  try { if (navigator.audioSession) navigator.audioSession.type = "playback"; } catch (e) {}
};
const IS_IOS = typeof navigator !== "undefined" && /iPhone|iPad|iPod/i.test(navigator.userAgent || "");

export default function AnotherRelease() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [mode, setMode] = useState("preview"); // preview | unlocked
  const [showModal, setShowModal] = useState(false);
  const [showCta, setShowCta] = useState(false);
  const [returning, setReturning] = useState(false); // connected fan coming back
  const [justUnlocked, setJustUnlocked] = useState(false);
  const [fromSmart, setFromSmart] = useState(false);
  const [smartMode, setSmartMode] = useState("continue_from_reel");
  const [smartCF, setSmartCF] = useState(null);
  const [smartDur, setSmartDur] = useState(25);
  const smartStartRef = useRef(false);
  const smartDoneRef = useRef(false);

  const audioRef = useRef(null);
  const heroPlayerRef = useRef(null);
  const playerZoneRef = useRef(null);
  const previewUrlRef = useRef(null);
  const previewUrlPromiseRef = useRef(null);
  const [clip, setClip] = useState(null); // {start,end} when serving the optimised preview clip
  // Dwell / abandonment telemetry (all times relative to page mount).
  const landingT = useRef(performance.now());
  const visRef = useRef({ ms: 0, since: null, resume: false });
  const tapTRef = useRef(null);
  const ctaSeenRef = useRef(new Set());
  const modalOpenedRef = useRef(false);
  const hideSeq = useRef(0);
  const msNow = () => Math.round(performance.now() - landingT.current);
  const visMs = () => Math.round(visRef.current.ms + (visRef.current.since != null ? performance.now() - visRef.current.since : 0));
  const [srcState, setSrcState] = useState("loading"); // loading | ready | failed
  const [pendingTap, setPendingTap] = useState(false);
  const fullUrlRef = useRef(null);
  const startedRef = useRef(false);
  const fullPlayRef = useRef(false);
  const resumedRef = useRef(false);
  const firedRef = useRef(new Set());
  // Preview micro-funnel: one-shot telemetry gate (landing→visible→ready→tap→started→complete).
  const teleRef = useRef(new Set());
  const trackOnce = (name, meta = {}) => {
    if (teleRef.current.has(name)) return;
    teleRef.current.add(name);
    track(slug, name, meta);
  };

  const [started, setStarted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const [err, setErr] = useState("");
  const [heroInView, setHeroInView] = useState(true);

  // join form
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const p = data?.project;
  const cover = p?.branding?.cover_image;
  const canvas = p?.branding?.canvas_video;
  const artist = p?.artist || "";
  const name = p?.name || "";
  const tagline = p?.tagline || "";
  const label = (p?.release_label || "Exclusive Release");
  const world = p?.world || artist;
  const allowDownload = !!p?.allow_download;

  const posKey = `fanly_pos_${slug}`;

  useEffect(() => {
    (async () => {
      // Fetch the preview source immediately (in parallel) so an early tap has a source.
      const sp0 = new URLSearchParams(window.location.search);
      const cf0 = sp0.get("from") === "smart" && (sp0.get("pm") || "continue_from_reel") === "continue_from_reel" ? sp0.get("cf") : null;
      const win = cf0 !== null && cf0 !== "" && !isNaN(Number(cf0)) ? [Number(cf0), Number(sp0.get("dur")) || 25] : [null, null];
      previewUrlPromiseRef.current = getPreviewUrl(slug, win[0], win[1]).then((r) => {
        previewUrlRef.current = r?.url || null;
        setClip(r?.is_clip ? { start: r.clip_start, end: r.clip_end } : null);
        setSrcState(r?.url ? "ready" : "failed"); return r?.url || null;
      }).catch(() => { setSrcState("failed"); return null; });
      await initSession(slug);
      try {
        const sp = new URLSearchParams(window.location.search);
        if (sp.get("from") === "smart") {
          setFromSmart(true);
          setSmartMode(sp.get("pm") || "continue_from_reel");
          const cf = sp.get("cf");
          if (cf !== null && cf !== "") setSmartCF(Number(cf));
          const dr = sp.get("dur");
          if (dr !== null && dr !== "" && !isNaN(Number(dr))) setSmartDur(Number(dr));
          // Real client-side smart landing (a person actually reached FANLY), as opposed
          // to the server-side smart_trigger_click (inflated by refresh/prefetch/scrape).
          trackOnce("smart_landing_arrived", {
            ...getDeviceInfo(), src: sp.get("src") || null, content: sp.get("content") || null,
            pm: sp.get("pm") || null,
          });
        }
      } catch (e) {}
      let d = null;
      let previewOk = false;
      try {
        d = await fetchProject(slug);
        setData(d);
        setDur(d?.preview?.hard_stop_sec || d?.preview?.duration_sec || 0);
        previewOk = !!(await previewUrlPromiseRef.current);
      } catch (e) { toast.error(t("t_load")); }
      track(slug, "landing_view");
      if (!previewOk) trackOnce("preview_media_error", { ...getDeviceInfo(), stage: "url_fetch" });
      recordReturnVisit(slug);

      // Returning fan: identity alone does NOT unlock — verify release-specific access.
      if (d?.full && isFanIdentified()) {
        try {
          const url = await getFullUrl(slug);            // 200 ⇒ this release is unlocked
          fullUrlRef.current = url;
          setMode("unlocked");
          setReturning(true);
          setDur(d.full.duration_sec || 0);
          const saved = Math.max(0, Number(localStorage.getItem(posKey) || 0));
          const a = audioRef.current;
          if (a) { a.src = url; a.currentTime = saved; }
          setCur(saved);
          setStarted(true);
        } catch (e) { /* identified but not unlocked → normal preview flow */ }
      }
    })();
  }, [slug]);

  // Shared localization: re-resolve localized content on language change without
  // disturbing playback, identity, progress or analytics. Only project content swaps.
  const langMounted = useRef(false);
  useEffect(() => {
    if (!langMounted.current) { langMounted.current = true; return; }
    (async () => {
      try {
        const d = await fetchProject(slug);
        setData((prev) => (prev ? { ...prev, project: d.project } : d));
      } catch (e) { /* keep current content on failure */ }
    })();
  }, [lang, slug]);

  // Show the sticky mini-player only when the hero player has scrolled out of view.
  useEffect(() => {
    const el = heroPlayerRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => setHeroInView(e.isIntersecting), { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [started, mode]);

  // Preview micro-funnel: fire once when the player zone (the pre-tap LISTEN control)
  // actually enters the fan's viewport — BEFORE any tap. This is the true "player seen".
  useEffect(() => {
    const el = playerZoneRef.current;
    if (!el || mode !== "preview") return;
    const obs = new IntersectionObserver(([e]) => {
      const v = visRef.current;
      if (e.isIntersecting) {
        trackOnce("preview_visible", getDeviceInfo());
        if (v.since == null && document.visibilityState === "visible") v.since = performance.now();
      } else if (v.since != null) { v.ms += performance.now() - v.since; v.since = null; }
    }, { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [mode, slug]);

  // Exit / dwell telemetry. A hidden page is a PROBABLE exit (fan may come back → page_resumed).
  useEffect(() => {
    const snap = (reason) => ({ reason, seq: ++hideSeq.current, dwell_ms: msNow(), player_visible_ms: visMs(),
      tapped: tapTRef.current != null,
      first_tap_ms: tapTRef.current != null ? Math.round(tapTRef.current - landingT.current) : null,
      started: startedRef.current, completed: teleRef.current.has("preview_completed"),
      ctas_seen: [...ctaSeenRef.current], modal_opened: modalOpenedRef.current });
    const onVis = () => {
      const v = visRef.current;
      if (document.visibilityState === "hidden") {
        if (v.since != null) { v.ms += performance.now() - v.since; v.since = null; v.resume = true; }
        track(slug, "page_hidden", snap("hidden"));
      } else {
        if (v.resume) { v.since = performance.now(); v.resume = false; }
        track(slug, "page_resumed", { dwell_ms: msNow() });
      }
    };
    const onHide = () => track(slug, "page_hidden", snap("pagehide"));
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", onHide);
    return () => { document.removeEventListener("visibilitychange", onVis); window.removeEventListener("pagehide", onHide); };
  }, [slug]);

  useEffect(() => { if (showModal) modalOpenedRef.current = true; }, [showModal]);

  const seeCta = (name) => (el) => {
    if (!el || el.__ctaObs || ctaSeenRef.current.has(name)) return;
    el.__ctaObs = true;
    const o = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !ctaSeenRef.current.has(name)) {
        ctaSeenRef.current.add(name); track(slug, "cta_visible", { cta: name, ms_since_landing: msNow() }); o.disconnect();
      }
    }, { threshold: 0.6 });
    o.observe(el);
  };

  const onTime = () => {
    const a = audioRef.current; if (!a) return;
    setCur(a.currentTime);
    if (mode === "preview") {
      const end = previewEnd || a.duration || 0;
      if (end && a.currentTime >= end - 0.2) {
        a.pause(); setPlaying(false); setShowCta(true);
        // Preview reached its end — fire for ALL previews (the hard-stop pauses, so onEnded never runs).
        trackOnce("preview_completed", getDeviceInfo());
        if (fromSmart && !smartDoneRef.current) { smartDoneRef.current = true; track(slug, "smart_preview_complete"); }
      } else if (end && a.currentTime >= end - 4) setShowCta(true);
      return;
    }
    const total = a.duration || dur;
    if (total) {
      localStorage.setItem(posKey, String(a.currentTime));
      const pct = a.currentTime / total;
      [[0.25, "audio_25"], [0.5, "audio_50"], [0.75, "audio_75"]].forEach(([q, ev]) => {
        if (pct >= q && !firedRef.current.has(ev)) { firedRef.current.add(ev); trackAuth(slug, ev); }
      });
      if (pct >= 0.9 && !firedRef.current.has("done")) {
        firedRef.current.add("done"); trackAuth(slug, "audio_completed"); trackAuth(slug, "listen_complete");
      }
    }
  };
  const onEnded = () => {
    setPlaying(false);
    if (mode === "preview") { setShowCta(true); track(slug, "preview_completed");
      if (fromSmart && !smartDoneRef.current) { smartDoneRef.current = true; track(slug, "smart_preview_complete"); }
    }
    else {
      localStorage.removeItem(posKey);
      if (!firedRef.current.has("done")) { firedRef.current.add("done"); trackAuth(slug, "audio_completed"); trackAuth(slug, "listen_complete"); }
    }
  };

  const startPreview = (via = "button") => {
    const a = audioRef.current;
    const firstTap = tapTRef.current == null;
    if (firstTap) tapTRef.current = performance.now();
    // Capture the tap INTENT immediately — before play() — so a tap that later fails is not invisible.
    track(slug, "preview_play_tap", { ...getDeviceInfo(), via, has_source: !!previewUrlRef.current,
      readyState: a?.readyState, networkState: a?.networkState, is_clip: !!clip,
      ms_since_landing: msNow(), player_visible_ms: visMs(), first_tap: firstTap });
    if (srcState !== "ready" || !data) {
      // Early tap (source/project still loading): remember it instead of silently dropping it.
      setErr(""); setLoading(true); setPendingTap(true);
      return;
    }
    playPreview();
  };

  const playPreview = () => {
    const a = audioRef.current;
    const start = Number(previewStart) || 0;
    forcePlaybackSession();
    if (a.src !== previewUrlRef.current) { a.src = previewUrlRef.current; }
    a.muted = false; a.volume = 1;
    setErr(""); setLoading(true);
    // Reliable seek: only trust the position once the stream is actually seekable.
    // Setting currentTime immediately after play() on a fresh stream is a race that
    // silently drops the seek (playing from 0) — the Continue-from-reel bug.
    const applySeek = () => {
      if (start <= 0) return;
      try { a.currentTime = start; } catch (e) {}
    };
    const seekable = () => a.readyState >= 1 && a.seekable && a.seekable.length > 0;
    a.play().then(() => {
      if (seekable()) applySeek();
      else a.addEventListener("canplay", applySeek, { once: true });
      // verify the seek took effect; retry once shortly after in case it was clamped
      if (start > 0) {
        setTimeout(() => { try { if (Math.abs(a.currentTime - start) > 1.5) a.currentTime = start; } catch (e) {} }, 400);
      }
      setLoading(false); setStarted(true); setPlaying(true);
      if (!startedRef.current) { startedRef.current = true; track(slug, "preview_started"); }
      trackOnce("preview_play_started", { ...getDeviceInfo(), is_clip: !!clip,
        wait_ms: tapTRef.current != null ? Math.round(performance.now() - tapTRef.current) : null });
      if (fromSmart && !smartStartRef.current) { smartStartRef.current = true; track(slug, "smart_preview_start"); }
    }).catch((e) => {
      // play() was rejected (autoplay/NotAllowedError, media not ready, in-app webview block).
      // This is the previously-invisible failed-play population — now explicitly recorded.
      track(slug, "preview_play_error", { ...getDeviceInfo(), has_source: !!previewUrlRef.current,
        readyState: a?.readyState, networkState: a?.networkState,
        error: e?.name || "unknown", message: (e?.message || "").slice(0, 120) });
      // Continue-my-video BETA safety: if playback/seek fails, fall back to the saved preview.
      if (fromSmart && smartMode === "continue_from_reel") {
        setErr(""); toast.error(t("t_tap_again"));
      } else { setErr(t("t_tap_again")); }
      setLoading(false);
    });
  };

  // Fire a remembered early tap once BOTH the project window and the audio source are ready.
  useEffect(() => {
    if (!pendingTap) return;
    if (srcState === "failed") {
      setPendingTap(false); setLoading(false); setErr(t("t_preview_unavailable"));
      trackOnce("preview_media_error", { ...getDeviceInfo(), stage: "no_source_on_tap" });
    } else if (srcState === "ready" && data) {
      setPendingTap(false); playPreview();
    }
  }, [pendingTap, srcState, data]);

  // First full-track play (returning resume OR fresh listen).
  const onFullFirstPlay = () => {
    if (fullPlayRef.current) return;
    fullPlayRef.current = true;
    trackAuth(slug, "audio_started");
    if (returning) trackAuth(slug, "listen_resume");
    else trackAuth(slug, "listen_start");
  };

  const toggle = () => {
    const a = audioRef.current; if (!a) return;
    if (mode === "preview" && !started) { if (!loading) startPreview(); return; }
    if (playing) { a.pause(); setPlaying(false); return; }
    forcePlaybackSession();
    // resume connected returning fan from saved position on first tap
    if (mode === "unlocked" && returning && !resumedRef.current) {
      resumedRef.current = true;
      const saved = Math.max(0, Number(localStorage.getItem(posKey) || 0));
      if (saved && a.duration && saved < a.duration) a.currentTime = saved;
    }
    a.play().then(() => { setPlaying(true); if (mode === "unlocked") onFullFirstPlay(); }).catch(() => {});
  };

  const startOver = () => {
    const a = audioRef.current; if (!a) return;
    a.currentTime = 0; setCur(0); localStorage.setItem(posKey, "0");
    a.play().then(() => { setPlaying(true); onFullFirstPlay(); }).catch(() => {});
  };

  const seek = (v) => { const a = audioRef.current; if (a && a.src) { a.currentTime = v; setCur(v); } };

  const doDownload = async () => {
    trackAuth(slug, "download_click");
    try {
      const url = await getFullUrl(slug, true); // downloads keep the original master file
      const res = await fetch(url);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${(name || "track").replace(/[^\w\-]+/g, "_")}.mp3`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    } catch (e) { toast.error(t("t_generic")); }
  };

  const doUnlock = async (e) => {
    e && e.preventDefault();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { track(slug, "join_submit_attempt", { result: "invalid_email" }); toast.error(t("t_invalid_email")); return; }
    if (!consent) { track(slug, "join_submit_attempt", { result: "no_consent" }); toast.error(t("t_consent")); return; }
    track(slug, "join_submit_attempt", { result: "submitted" });
    setSubmitting(true);
    try {
      await submitJoin({ slug, email, first_name: firstName, consent }); // stores fan token
      const fullUrl = await getFullUrl(slug);
      fullUrlRef.current = fullUrl;
      const a = audioRef.current;
      if (!a) { setShowModal(false); setMode("unlocked"); setReturning(true); setSubmitting(false); return; }
      firedRef.current = new Set(); fullPlayRef.current = false; resumedRef.current = true;
      setReturning(false);
      setMode("unlocked"); setShowCta(false); setShowModal(false); setJustUnlocked(true);
      setDur(data?.full?.duration_sec || 0);
      a.src = fullUrl; a.currentTime = 0; setCur(0); a.muted = false; a.volume = 1;
      forcePlaybackSession();
      trackAuth(slug, "unlock_success");
      a.play().then(() => { setPlaying(true); onFullFirstPlay(); }).catch(() => setPlaying(false));
      toast.success(t("t_unlocked"));
    } catch (err) {
      track(slug, "join_submit_attempt", { result: "error", status: err?.response?.status || null });
      toast.error(err?.response?.data?.detail || t("t_generic"));
    } finally { setSubmitting(false); }
  };

  const sections = (p?.sections || [])
    .slice().sort((a, b) => SECTION_ORDER.indexOf(a.key) - SECTION_ORDER.indexOf(b.key));

  const pv = data?.preview || {};
  const useContinue = fromSmart && smartMode === "continue_from_reel" && smartCF != null && !isNaN(smartCF);
  const previewStart = clip ? 0 : (useContinue ? smartCF : (pv.start_sec || 0));
  const previewEnd = clip ? (clip.end - clip.start) : (useContinue ? smartCF + (smartDur || 25) : (pv.end_sec || pv.hard_stop_sec || 0));
  const wStart = mode === "unlocked" ? 0 : previewStart;
  const wEnd = mode === "unlocked" ? (dur || data?.full?.duration_sec || 0) : previewEnd;
  const span = Math.max(0.001, wEnd - wStart);
  const rel = Math.max(0, Math.min(span, cur - wStart));
  const total = wEnd || 0;
  const pct = (rel / span) * 100;
  const accent = mode === "unlocked" ? C.mint : C.indigo;
  const showMini = started && !heroInView;
  const winSecs = Math.round((previewEnd || 0) - (previewStart || 0));
  const previewSecs = clip ? (clip.end - clip.start) : useContinue ? (smartDur || 25) : (winSecs > 0 ? winSecs : 25);
  const heroTap = (e) => {
    if (mode !== "preview" || started || loading) return;
    if (e.target.closest("button, a, input, select, [data-no-hero-tap]")) return;
    startPreview("hero");
  };

  return (
    <div className="relative min-h-[100dvh]" style={{ background: C.bg }} data-testid="fan-experience">
      <LanguageSelector className="fixed top-4 right-4 z-[60]" />
      <audio ref={audioRef} preload="none"
        onLoadedMetadata={(e) => { if (mode === "unlocked") setDur(e.target.duration || dur); }}
        onCanPlay={() => { if (mode === "preview") trackOnce("preview_media_ready", { ...getDeviceInfo(), readyState: audioRef.current?.readyState }); }}
        onTimeUpdate={onTime} onEnded={onEnded} onPause={() => setPlaying(false)} onPlay={() => setPlaying(true)}
        onError={() => {
          setErr(t("t_audio_failed")); setPlaying(false);
          if (mode === "preview") {
            const a = audioRef.current;
            trackOnce("preview_media_error", { ...getDeviceInfo(), stage: "media_element",
              readyState: a?.readyState, networkState: a?.networkState,
              code: a?.error?.code || null });
          }
        }}
        controlsList="nodownload" onContextMenu={(e) => e.preventDefault()} />

      {/* HERO */}
      <div className={`relative min-h-[100dvh] flex flex-col justify-end overflow-hidden ${mode === "preview" && !started ? "cursor-pointer" : ""}`}
        onClick={heroTap} data-testid="screen-landing">
        <div className="absolute inset-0 z-0">
          {canvas ? (
            <video data-testid="canvas-video" className="w-full h-full object-cover object-top"
              src={canvas} poster={cover || undefined} autoPlay muted loop playsInline preload="auto" />
          ) : cover ? (
            <img src={cover} alt="" className="w-full h-full object-cover object-top" />
          ) : null}
          <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${C.bg} 4%, rgba(11,13,18,0.55) 45%, rgba(11,13,18,0.2) 100%)` }} />
          <div className="absolute inset-0" style={{ background: `radial-gradient(80% 50% at 20% 92%, ${accent}22 0%, transparent 60%)` }} />
        </div>

        <div className="relative z-10 w-full max-w-2xl mx-auto px-6 sm:px-10 pb-14 pt-24">
          {/* Returning fan welcome */}
          <AnimatePresence>
            {returning && mode === "unlocked" && (
              <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                className="inline-flex items-center gap-2 mb-5 rounded-full px-4 py-2 text-[11px] font-mono-x tracking-[0.25em] uppercase"
                style={{ background: `${C.mint}14`, color: C.mint, border: `1px solid ${C.mint}40` }}
                data-testid="welcome-back-banner">
                <Check className="w-3.5 h-3.5" /> {t("welcome_back")}
              </motion.div>
            )}
          </AnimatePresence>

          <p className="font-mono-x text-xs tracking-[0.45em] uppercase mb-3" style={{ color: accent }}>
            {mode === "unlocked" ? label : (tagline || label)}
          </p>
          {fromSmart && mode !== "unlocked" && (
            <div className="mb-4" data-testid="smart-continue-banner">
              <p className="font-display text-white text-lg font-bold tracking-tight" style={{ color: accent }}>
                {useContinue ? t("continue_song") : t("hear_preview")}
              </p>
              <p className="font-mono-x text-xs tracking-widest text-white/60 uppercase mt-0.5">
                {useContinue ? t("hear_next", { n: previewSecs }) : t("preview_secs", { n: previewSecs })}
              </p>
            </div>
          )}
          <h1 className="font-display text-white text-5xl sm:text-6xl font-black tracking-tighter leading-[0.9]">{name}</h1>
          <p className="font-mono-x text-sm tracking-[0.3em] text-white/70 uppercase mt-2 mb-8">{artist}</p>

          {/* PLAYER */}
          <div ref={playerZoneRef}>
          {!started ? (
            <div data-testid="listen-zone">
              {mode === "preview" && (
                <p className="text-white/80 text-sm sm:text-base mb-5 max-w-md" data-testid="hero-value-prop">
                  {t("hero_value", { n: previewSecs })}
                </p>
              )}
              <button data-testid="listen-button" onClick={toggle}
                className="relative group w-full sm:w-auto inline-flex items-center justify-center gap-3 font-bold rounded-full px-10 py-5 text-lg transition-transform duration-300 hover:scale-105 active:scale-95"
                style={{ background: C.indigo, color: "#fff", boxShadow: `0 10px 40px ${C.indigo}66` }}>
                {!loading && <span className="absolute inset-0 rounded-full animate-ping opacity-30 pointer-events-none" style={{ background: C.indigo }} />}
                {loading ? <Loader2 className="relative w-6 h-6 animate-spin" /> : <Play className="relative w-6 h-6" fill="currentColor" />}
                <span className="relative tracking-wide">{t("listen")}</span>
              </button>
              {mode === "preview" && (
                <p className="mt-4 font-mono-x text-[11px] tracking-[0.25em] uppercase text-white/50 text-center sm:text-left" data-testid="tap-anywhere-hint">
                  {t("tap_anywhere")} · {previewSecs}s
                </p>
              )}
              {err && (
                <p className="mt-3 flex items-center gap-2 font-mono-x text-[11px]" style={{ color: C.coral }} data-testid="listen-error">
                  <AlertTriangle className="w-3.5 h-3.5" /> {err}
                </p>
              )}
            </div>
          ) : (
            <div ref={heroPlayerRef} className="max-w-xl rounded-2xl backdrop-blur-xl p-5"
              style={{ background: `${C.surface}cc`, border: `1px solid ${accent}33` }} data-testid="hero-player">

              {mode === "unlocked" && justUnlocked && (
                <div className="inline-flex items-center gap-2 mb-4 rounded-full px-3.5 py-1.5 text-[10px] font-mono-x tracking-[0.3em] uppercase"
                  style={{ background: `${C.mint}18`, color: C.mint, border: `1px solid ${C.mint}44` }} data-testid="full-unlocked-banner">
                  <Check className="w-3 h-3" /> {t("full_unlocked")}
                </div>
              )}

              <div className="flex items-center gap-4">
                <button data-testid="player-toggle" onClick={toggle} aria-label={playing ? "Pause" : "Play"}
                  className="flex items-center justify-center w-16 h-16 rounded-full text-white shrink-0 transition-transform hover:scale-105 active:scale-95"
                  style={{ background: accent, boxShadow: `0 0 28px ${accent}66` }}>
                  {loading ? <Loader2 className="w-7 h-7 animate-spin" /> : playing ? <Pause className="w-7 h-7" fill="currentColor" /> : <Play className="w-8 h-8 ml-1" fill="currentColor" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono-x text-[10px] tracking-widest uppercase" style={{ color: accent }}>
                      {mode === "unlocked" ? t("full_track") : t("preview")}
                    </span>
                  </div>
                  <input type="range" className="scrub" min={wStart} max={wEnd || 0} step={0.1} value={cur}
                    onChange={(e) => seek(Number(e.target.value))} data-testid="player-scrub"
                    style={{ background: `linear-gradient(to right, ${accent} ${pct}%, rgba(255,255,255,0.14) ${pct}%)` }} />
                  <div className="flex justify-between mt-1.5 font-mono-x text-[11px] text-white/45">
                    <span data-testid="player-current">{fmt(rel)}</span>
                    <span data-testid="player-duration">{fmt(span)}</span>
                  </div>
                </div>
              </div>
              {err && <p className="mt-3 flex items-center gap-2 font-mono-x text-[11px]" style={{ color: C.coral }} data-testid="player-error"><AlertTriangle className="w-3.5 h-3.5" /> {err}</p>}
              {IS_IOS && playing && mode === "preview" && (
                <p className="mt-3 font-mono-x text-[11px] text-white/50" data-testid="ios-silent-hint">{t("ios_silent_hint")}</p>
              )}

              {/* Preview → Unlock CTA */}
              <AnimatePresence>
                {showCta && mode === "preview" && (
                  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    className="mt-5 pt-5 border-t border-white/10 flex items-center justify-between gap-4" data-testid="want-rest">
                    <span className="font-display text-white text-lg font-bold tracking-tight">{t("want_rest")}</span>
                    <button data-testid="unlock-cta" ref={seeCta("unlock_main")} onClick={() => { track(slug, "join_cta_clicked"); setShowModal(true); }}
                      className="group inline-flex items-center gap-2 text-white font-semibold rounded-full pl-5 pr-4 py-3 text-sm hover:scale-105 active:scale-95 transition-transform"
                      style={{ background: C.coral, boxShadow: `0 8px 30px ${C.coral}55` }}>
                      {t("unlock")} <ArrowRight className="w-4 h-4" />
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>

              {mode === "preview" && !showCta && (
                <button data-testid="unlock-cta-early" ref={seeCta("unlock_early")} onClick={() => { track(slug, "join_cta_clicked"); setShowModal(true); }}
                  className="mt-4 font-mono-x text-[11px] tracking-widest uppercase text-white/45 hover:text-white transition-colors">
                  {t("unlock_early", { name })}
                </button>
              )}

              {/* Connected: secondary actions (continue / start over / download) */}
              {mode === "unlocked" && (
                <div className="mt-5 pt-4 border-t border-white/10 flex flex-wrap items-center gap-3">
                  {returning && !playing && (
                    <button onClick={toggle} data-testid="continue-listening-btn"
                      className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white hover:scale-105 active:scale-95 transition-transform"
                      style={{ background: C.mint, color: "#04120c", boxShadow: `0 8px 26px ${C.mint}55` }}>
                      <Play className="w-4 h-4" fill="currentColor" /> {t("continue_listening")}
                    </button>
                  )}
                  <button onClick={startOver} data-testid="start-over-btn"
                    className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-mono-x tracking-widest uppercase text-white/55 hover:text-white transition-colors"
                    style={{ border: "1px solid rgba(255,255,255,0.14)" }}>
                    <RotateCcw className="w-3.5 h-3.5" /> {t("start_over")}
                  </button>
                  {allowDownload && (
                    <button onClick={doDownload} data-testid="download-btn"
                      className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-mono-x tracking-widest uppercase transition-colors"
                      style={{ color: C.mint, border: `1px solid ${C.mint}44` }}>
                      <Download className="w-3.5 h-3.5" /> {t("download")}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          </div>
        </div>
      </div>

      {/* EXPLORE — sections after unlock */}
      {mode === "unlocked" && (
        <div className="pb-32">
          <div className="max-w-2xl mx-auto px-6 sm:px-10 pt-14 pb-2">
            <p className="font-mono-x text-[11px] tracking-[0.4em] uppercase" style={{ color: C.mint }}>{t("explore_more")}</p>
          </div>
          {sections.map((s, i) => (
            <motion.section key={s.key} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }}
              className="border-t border-white/10 py-16 px-6 sm:px-10" data-testid={`section-${s.key}`}>
              <div className={s.display_type === "lyrics" ? "max-w-[800px] mx-auto" : "max-w-2xl mx-auto"}>
                <p className="font-mono-x text-[11px] tracking-[0.35em] uppercase mb-4" style={{ color: C.mint }}>0{i + 1} — {s.label}</p>
                {s.media_type !== "video" && s.image && (
                  <div className="relative mb-6 overflow-hidden rounded-xl border border-white/10 w-full" style={{ background: C.surface }}>
                    <img src={s.image} alt={s.label} className="w-full h-auto block" loading="lazy" />
                  </div>
                )}
                {s.display_type === "lyrics" ? (
                  <LyricsView label={s.label} original={s.body} translation={s.body_translation} />
                ) : s.media_type === "video" ? (
                  <>
                    <h2 className="font-display text-white text-3xl sm:text-4xl font-bold tracking-tight mb-4">{s.label}</h2>
                    {s.body && <p className="text-base sm:text-lg leading-relaxed text-white/75 whitespace-pre-line mb-6">{s.body}</p>}
                    {s.video && <VisualVideo src={s.video} slug={slug} sectionKey={s.key} />}
                  </>
                ) : (
                  <>
                    <h2 className="font-display text-white text-3xl sm:text-4xl font-bold tracking-tight mb-4">{s.label}</h2>
                    <p className="text-base sm:text-lg leading-relaxed text-white/75 whitespace-pre-line">{s.body}</p>
                  </>
                )}
              </div>
            </motion.section>
          ))}
          {p?.closing_block && p.closing_block.active !== false && (
            <ClosingBlock block={p.closing_block} slug={slug} artist={artist} onNeedUnlock={() => setShowModal(true)} />
          )}
          <footer className="text-center pt-6"><p className="font-mono-x text-[10px] tracking-[0.3em] text-white/25 uppercase">{world} · {t("powered_by")}</p></footer>
        </div>
      )}

      {/* PERSISTENT MINI-PLAYER */}
      <AnimatePresence>
        {showMini && (
          <motion.div initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
            className="fixed bottom-0 inset-x-0 z-50 px-3 pb-3" data-testid="mini-player">
            <div className="max-w-2xl mx-auto rounded-2xl backdrop-blur-xl px-4 py-3"
              style={{ background: `${C.surface}f2`, border: `1px solid ${accent}40`, boxShadow: "0 -8px 40px rgba(0,0,0,0.45)" }}>
              <div className="flex items-center gap-3">
                <button data-testid="mini-player-toggle" onClick={toggle} aria-label={playing ? "Pause" : "Play"}
                  className="flex items-center justify-center w-11 h-11 rounded-full text-white shrink-0 transition-transform active:scale-90"
                  style={{ background: accent }}>
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : playing ? <Pause className="w-5 h-5" fill="currentColor" /> : <Play className="w-5 h-5 ml-0.5" fill="currentColor" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono-x text-[10px] tracking-widest uppercase truncate" style={{ color: accent }}>
                      {mode === "unlocked" ? name : t("preview")}
                    </span>
                    <span className="font-mono-x text-[10px] text-white/40">{fmt(rel)} / {fmt(span)}</span>
                  </div>
                  <input type="range" className="scrub" min={wStart} max={wEnd || 0} step={0.1} value={cur}
                    onChange={(e) => seek(Number(e.target.value))} data-testid="mini-player-scrub"
                    style={{ background: `linear-gradient(to right, ${accent} ${pct}%, rgba(255,255,255,0.14) ${pct}%)` }} />
                </div>
                {mode === "unlocked" && allowDownload && (
                  <button onClick={doDownload} data-testid="mini-download-btn" aria-label={t("download")}
                    className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-colors"
                    style={{ color: C.mint, border: `1px solid ${C.mint}44` }}>
                    <Download className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* UNLOCK MODAL */}
      <AnimatePresence>
        {showModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center backdrop-blur-sm px-4"
            style={{ background: "rgba(4,5,8,0.72)" }}
            data-testid="unlock-modal" onClick={() => setShowModal(false)}>
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-2xl p-6 sm:p-8 mb-4 sm:mb-0"
              style={{ background: C.surface, border: `1px solid ${C.coral}33` }}>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="font-mono-x text-[10px] tracking-[0.4em] uppercase" style={{ color: C.coral }}>{t("modal_kicker")}</p>
                  <h3 className="font-display text-white text-2xl font-bold tracking-tight">{t("unlock_name", { name })}</h3>
                </div>
                <button onClick={() => setShowModal(false)} data-testid="modal-close" className="text-white/45 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <p className="text-white/65 text-sm mb-6">{t("modal_no_password")}</p>
              <form onSubmit={doUnlock} className="space-y-6">
                <input data-testid="join-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("email_ph")}
                  className="w-full bg-transparent border-b-2 border-white/25 outline-none py-3 text-lg text-white placeholder:text-white/30 transition-colors"
                  style={{ caretColor: C.coral }} onInvalid={() => track(slug, "join_submit_attempt", { result: "invalid_email", native: true })} onFocus={(e) => { e.target.style.borderColor = C.coral; trackOnce("join_form_focus", { field: "email", ms_since_landing: msNow() }); }} onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.25)")} />
                <input data-testid="join-first-name" type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder={t("first_name_ph")}
                  className="w-full bg-transparent border-b-2 border-white/25 outline-none py-3 text-white placeholder:text-white/30 transition-colors"
                  onFocus={(e) => (e.target.style.borderColor = C.coral)} onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.25)")} />
                <button type="button" data-testid="join-consent" onClick={() => setConsent((c) => !c)} className="flex items-start gap-3 text-left w-full">
                  <span className="mt-0.5 w-5 h-5 shrink-0 rounded border flex items-center justify-center transition-colors"
                    style={consent ? { background: C.coral, borderColor: C.coral } : { borderColor: "rgba(255,255,255,0.4)" }}>
                    {consent && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                  </span>
                  <span className="font-mono-x text-[11px] leading-relaxed text-white/55">
                    {t("consent_text", { name, artist })}
                  </span>
                </button>
                <button type="submit" data-testid="join-submit" disabled={submitting}
                  className="w-full flex items-center justify-center gap-3 text-white font-semibold rounded-full py-4 hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-60"
                  style={{ background: C.coral, boxShadow: `0 10px 34px ${C.coral}55` }}>
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{t("unlock_name", { name })} <ArrowRight className="w-5 h-5" /></>}
                </button>
              </form>
              <p className="mt-6 font-mono-x text-[10px] tracking-[0.3em] text-white/25 uppercase">{t("powered_by")}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Lyrics ──────────────────────────────────────────────────────────────
const LYRIC_HEADER_RE = /^\s*(pre[-\s]?chorus|final\s+chorus|coro\s+final|verse|verso|chorus|coro|bridge|puente|ponte|outro|intro|refr[aã]o|estribillo|final)(\s*\d+)?\s*:?\s*$/i;

function parseLyricBlocks(text) {
  if (!text) return [];
  const lines = String(text).replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let cur = null;
  const flush = () => {
    if (!cur) return;
    while (cur.lines.length && cur.lines[0].trim() === "") cur.lines.shift();
    while (cur.lines.length && cur.lines[cur.lines.length - 1].trim() === "") cur.lines.pop();
    if (cur.header || cur.lines.length) blocks.push(cur);
    cur = null;
  };
  for (const line of lines) {
    if (LYRIC_HEADER_RE.test(line)) {
      flush();
      cur = { header: line.trim().replace(/:\s*$/, ""), lines: [] };
    } else {
      if (!cur) cur = { header: null, lines: [] };
      cur.lines.push(line);
    }
  }
  flush();
  return blocks;
}

function renderInline(text) {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    const m = part.match(/^\*\*([^*]+)\*\*$/);
    if (m) return <strong key={i} className="text-white font-semibold">{m[1]}</strong>;
    return <span key={i}>{part}</span>;
  });
}

function LyricLines({ lines, className }) {
  return lines.map((ln, j) => (
    <span key={j} className={`block ${className}`}>
      {ln.trim() === "" ? "\u00A0" : renderInline(ln)}
    </span>
  ));
}

function LyricsView({ label, original, translation }) {
  const { t } = useI18n();
  const [showT, setShowT] = useState(false);
  const oBlocks = parseLyricBlocks(original);
  const tBlocks = translation ? parseLyricBlocks(translation) : [];
  const hasT = tBlocks.length > 0;

  return (
    <div data-testid="lyrics-view">
      <div className="flex items-start justify-between gap-4 mb-8">
        <h2 className="font-display text-white text-3xl sm:text-4xl font-bold tracking-tight">{label}</h2>
        {hasT && (
          <button data-testid="lyrics-toggle-translation" onClick={() => setShowT((v) => !v)}
            className="shrink-0 mt-2 font-mono-x text-[10px] tracking-widest uppercase text-white/50 hover:text-white border-b border-white/20 hover:border-white/60 pb-0.5 transition-colors">
            {showT ? t("hide_translation") : t("show_translation")}
          </button>
        )}
      </div>
      <div className="space-y-9">
        {oBlocks.map((b, i) => (
          <div key={i} data-testid={`lyric-block-${i}`}>
            {b.header && (
              <p className="font-mono-x text-[10px] tracking-[0.4em] uppercase mb-3" style={{ color: C.mint }}>{b.header}</p>
            )}
            <div className="font-display text-xl sm:text-2xl leading-[1.7] text-white/90 tracking-tight">
              <LyricLines lines={b.lines} className="" />
            </div>
            {showT && tBlocks[i] && (
              <div className="mt-3 pl-4 border-l border-white/10" data-testid={`lyric-translation-${i}`}>
                {tBlocks[i].header && (
                  <p className="font-mono-x text-[9px] tracking-[0.35em] text-white/35 uppercase mb-1.5">{tBlocks[i].header}</p>
                )}
                <div className="text-sm sm:text-base leading-relaxed text-white/45 italic">
                  <LyricLines lines={tBlocks[i].lines} className="" />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function VisualVideo({ src, slug, sectionKey }) {
  const ref = useRef(null);
  const fired = useRef(new Set());
  const meta = () => ({ session: sectionKey, language: getLang() });
  const onPlay = () => {
    if (!fired.current.has("start")) { fired.current.add("start"); trackAuth(slug, "visual_video_start", meta()); }
  };
  const onTime = () => {
    const v = ref.current; if (!v || !v.duration) return;
    const pct = v.currentTime / v.duration;
    [[0.25, "visual_video_25"], [0.5, "visual_video_50"], [0.75, "visual_video_75"]].forEach(([q, ev]) => {
      if (pct >= q && !fired.current.has(ev)) { fired.current.add(ev); trackAuth(slug, ev, meta()); }
    });
  };
  const onEnded = () => {
    if (!fired.current.has("complete")) { fired.current.add("complete"); trackAuth(slug, "visual_video_complete", meta()); }
  };
  return (
    <div className="relative w-full overflow-hidden rounded-xl border border-white/10 bg-black" style={{ aspectRatio: "16 / 9" }} data-testid={`visual-video-${sectionKey}`}>
      <video ref={ref} src={src ? src + "#t=0.1" : src} controls playsInline preload="metadata"
        className="absolute inset-0 w-full h-full object-contain bg-black"
        onPlay={onPlay} onTimeUpdate={onTime} onEnded={onEnded}
        controlsList="nodownload" onContextMenu={(e) => e.preventDefault()} />
    </div>
  );
}

function ClosingBlock({ block, slug, artist, onNeedUnlock }) {
  const { t } = useI18n();
  const [kept, setKept] = useState(false);
  const [busy, setBusy] = useState(false);
  const viewed = useRef(false);
  useEffect(() => {
    if (!viewed.current) { viewed.current = true; track(slug, "closing_block_view", { language: getLang() }); }
  }, [slug]);
  const click = async () => {
    if (kept || busy) return;
    if (!isFanIdentified()) { onNeedUnlock && onNeedUnlock(); return; }
    setBusy(true);
    try {
      await optInFutureUpdates(slug, { consent_text: t("closing_consent"), consent_version: "closing_v1" });
      setKept(true);
    } catch (e) { toast.error(t("t_keep_err")); } finally { setBusy(false); }
  };
  const openPrefs = async () => {
    try { const url = await getPrefsLink(slug); window.location.href = url; }
    catch (e) { toast.error(t("t_generic")); }
  };
  return (
    <div className="px-6 sm:px-10 pt-6" data-testid="closing-block">
      <div className="max-w-2xl mx-auto rounded-2xl overflow-hidden"
        style={{ background: C.surface, border: `1px solid ${C.mint}33` }}>
        {block.image && (
          <div className="relative w-full overflow-hidden" style={{ aspectRatio: "21 / 9" }}>
            <img src={block.image} alt="" className="absolute inset-0 w-full h-full object-cover object-center" loading="lazy" />
            <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${C.surface}, transparent)` }} />
          </div>
        )}
        <div className="p-8 sm:p-10">
          <h3 className="font-display text-white text-2xl sm:text-3xl font-bold tracking-tight mb-3" data-testid="closing-title">{block.title}</h3>
          {block.body && <p className="text-white/65 leading-relaxed whitespace-pre-line mb-7 max-w-md" data-testid="closing-body">{block.body}</p>}
          {block.cta_enabled !== false && (
            <div>
              <button onClick={click} disabled={kept || busy} data-testid="closing-cta"
                className="inline-flex items-center gap-2 rounded-full font-semibold text-sm px-5 py-2.5 transition-transform duration-300 hover:scale-[1.03] active:scale-95"
                style={kept
                  ? { background: `${C.mint}1f`, color: C.mint, border: `1px solid ${C.mint}55` }
                  : { background: C.mint, color: "#04120c", boxShadow: `0 6px 20px ${C.mint}33` }}>
                {kept ? <><Check className="w-4 h-4" /> {t("youre_in")}</> : busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{block.cta_label || t("keep_me_in")} <ArrowRight className="w-4 h-4" /></>}
              </button>
              {kept ? (
                <p className="mt-4 text-sm" style={{ color: `${C.mint}dd` }} data-testid="closing-confirm-sub">
                  {t("closing_confirm_sub")}{" "}
                  <button onClick={openPrefs} data-testid="manage-prefs-link"
                    className="underline underline-offset-2 text-white/50 hover:text-white transition-colors">
                    {t("manage_prefs")}
                  </button>
                </p>
              ) : (
                <p className="mt-4 text-xs text-white/40 leading-relaxed max-w-md" data-testid="closing-consent">{t("closing_consent", { artist })}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
